import { useSettings } from "@/libs/hooks/useSettings";
import { useMainDict } from "@/libs/hooks/useTranslation";
import { Menu } from "./Menu";
import { useMenu } from "@/libs/hooks/useMenu";
import useTranslation from "next-translate/useTranslation";
import useGAnalytics, {
  EventAction,
  EventCategory,
  EventLabel,
} from "@/libs/hooks/tracking/useGAnalytics";
import useVisibilityTracker from "@/libs/hooks/tracking/useVisibilityTracking";
import { IconDownload } from "@tabler/icons-react";

export const MenuSection = () => {
  const { settings } = useSettings();
  const { t } = useMainDict();
  const { menu, isMenuLoading } = useMenu();
  const { lang } = useTranslation();

  const { trackEvent } = useGAnalytics();

  useVisibilityTracker("menu-section", () => {
    trackEvent(
      "menu-section-visible",
      EventCategory.USER_INTERACTION,
      EventAction.BECOMES_VISIBLE,
      EventLabel.Menu
    );
  });

  if (!settings?.showIngredients) {
    return null;
  }

  return (
    <section
      id="menu-section"
      aria-labelledby="menu-heading"
      className="relative w-full overflow-hidden"
    >
      {/* Vintage olive/sage background */}
      <div className="absolute inset-0 bg-gradient-to-b from-vintage-sage via-[#7A9B70] to-vintage-olive">
        {/* Decorative pattern */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.15)_100%)]" />
      </div>

      {/* Top decorative border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-16">
        {/* Section header with vintage typography */}
        <div className="text-center mb-12">
          {/* Decorative element */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-20 h-px bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />
            <span className="text-vintage-cream/80 text-2xl">◆</span>
            <div className="w-20 h-px bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />
          </div>

          <h2
            id="menu-heading"
            className="font-display text-5xl md:text-7xl font-bold text-vintage-cream tracking-wide"
            style={{
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {t("menuTitle")}
          </h2>

          {/* Subtitle decorative */}
          <p className="font-script text-vintage-parchment text-2xl mt-3 transform -rotate-1">
            Pizza Napoletana Autentica
          </p>

          {/* Decorative element */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />
            <span className="text-vintage-cream/80 text-lg">✦</span>
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />
          </div>
        </div>

        {/* Download menu button */}
        {settings.showDownloadMenuLink && settings.pdfMenuLink && (
          <div className="flex justify-center mb-10">
            <a
              target="_blank"
              href={settings.pdfMenuLink}
              className="group inline-flex items-center gap-3 bg-vintage-cream text-vintage-espresso
                px-6 py-3 font-condensed text-sm tracking-widest uppercase
                shadow-vintage hover:shadow-vintage transition-all duration-300
                hover:-translate-y-1 border border-vintage-walnut/10"
              aria-label={t("downloadMenu")}
            >
              <IconDownload size={18} className="group-hover:animate-bounce" />
              <span>{t("downloadMenu")}</span>
            </a>
          </div>
        )}

        {/* Menu items */}
        <Menu lang={lang} menu={menu} isLoading={isMenuLoading} />
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />
    </section>
  );
};
